// src/pages/MonitorsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { db, auth } from "../firebase";
import {
  collection,
  getDocs,
  addDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  doc,
  getDoc,
} from "firebase/firestore";
import {
  getMonitorAssignmentsPath,
  getMonitorRequestsForUserPath,
} from "../services/firestorePaths";
import { getActiveTenantId, getActiveSiteId } from "../services/activeTenant";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { logoutAndClearSite } from "../services/logout";

/**
 * Sections are unchanged; still used when monitors request documents.
 */
const SECTIONS = [
  "1. Protocol & Investigator Brochure",
  "2. Site Information",
  "3. Study Team Credentials",
  "4. IRB Documents",
  "5. Sponsor Documents",
  "6. Regulatory Submissions",
  "7. Safety Reporting",
  "8. Correspondence",
  "9. Training Documents",
  "10. Study Logs",
  "11. Monitoring Visits",
  "12. Close-Out Documents",
];

export default function MonitorsPage() {
  const navigate = useNavigate();

  const [assignedStudy, setAssignedStudy] = useState(null); // {id, studyId, name}
  const [checkedSections, setCheckedSections] = useState([]);
  const [monitorName, setMonitorName] = useState("");
  const [myRequests, setMyRequests] = useState([]);
  const [loadingAssign, setLoadingAssign] = useState(true);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // ---- Profile name from email (unchanged) ----
  useEffect(() => {
    if (auth.currentUser?.email) {
      const email = auth.currentUser.email;
      if (email.startsWith("monitor")) {
        const number = email.match(/\d+/)?.[0] || "";
        setMonitorName(`Monitor${number}`);
      } else {
        setMonitorName(email);
      }
    }
  }, []);

  // ---- Load active assignment for this monitor ----
  useEffect(() => {
    const loadAssignment = async () => {
      try {
        setLoadingAssign(true);
        const user = auth.currentUser;
        if (!user) {
          setAssignedStudy(null);
          return;
        }
        const email = user.email?.toLowerCase() || "";

        // Try by uid; fallback to email (robust)
        const assignmentsRef = collection(db, getMonitorAssignmentsPath());
        const q1 = query(assignmentsRef, where("monitorUid", "==", user.uid), where("active", "==", true));
        const q2 = query(assignmentsRef, where("monitorEmail", "==", email), where("active", "==", true));

        let snap = await getDocs(q1);
        if (snap.empty) snap = await getDocs(q2);

        if (snap.empty) {
          setAssignedStudy(null);
        } else {
          // expect only one active assignment per monitor
          const a = snap.docs[0].data();
          setAssignedStudy({
            id: a.studyId,         // Firestore doc id for ebinder/studies
            studyId: a.studyCode || a.studyProto || a.studyIdLabel || a.studyId || "", // best effort label
            name: a.studyName || "",
          });
        }
      } finally {
        setLoadingAssign(false);
      }
    };
    loadAssignment();
  }, []);

  // ---- Load my requests (latest first) ----
  const loadMyRequests = async () => {
    const user = auth.currentUser;
    if (!user) return;
    setLoadingRequests(true);

    const snap = await getDocs(
      collection(db, getMonitorRequestsForUserPath(user.uid))
    );

    const sorted = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => {
        const aMs = a.createdAt?.toMillis?.() || 0;
        const bMs = b.createdAt?.toMillis?.() || 0;
        return bMs - aMs; // newest first
      });

    setMyRequests(sorted);
    setLoadingRequests(false);
  };

  useEffect(() => {
    loadMyRequests();
  }, []);

  // ---- UI handlers ----
  const handleCheckboxChange = (section) => {
    setCheckedSections((prev) =>
      prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section]
    );
  };

  const statusChipClass = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "approved") return "bg-green-100 text-green-800 ring-green-300";
    if (s === "denied") return "bg-red-100 text-red-700 ring-red-300";
    return "bg-amber-100 text-amber-800 ring-amber-300";
  };

  // ---- Submit new request (scoped to assigned study) ----
  const handleRequest = async () => {
    const user = auth.currentUser;
    if (!user) {
      alert("⚠️ Not logged in.");
      return;
    }
    if (!assignedStudy) {
      alert("⚠️ No study assigned. Please contact admin.");
      return;
    }
    if (checkedSections.length === 0) {
      alert("⚠️ Please check at least one document section.");
      return;
    }

    try {
      setSubmitting(true);
      const requestsRef = collection(db, getMonitorRequestsForUserPath(user.uid));
      await addDoc(requestsRef, {
        studyId: assignedStudy.id,
        studyName: assignedStudy.name,
        requestedSections: checkedSections,
        status: "Pending",
        createdAt: serverTimestamp(),
        monitorName,
        // The site this request belongs to. AdminMonitors finds requests with a
        // collectionGroup query, which cannot filter by path, so it filters on
        // these instead — and the rule checks they match the folder the
        // document is created in, so they cannot point at another site.
        tenantId: getActiveTenantId(),
        siteId: getActiveSiteId(),
      });

      setCheckedSections([]);
      await loadMyRequests();
      alert("✅ Your request has been submitted for admin approval.");
    } catch (err) {
      console.error("❌ Error saving request:", err);
      alert("Failed to submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await logoutAndClearSite(); // also clears the cached userRole
    navigate("/login");
  };

  const HeaderCard = (
    <div className="relative rounded-2xl bg-white/90 backdrop-blur shadow-lg ring-1 ring-black/5 p-6 md:p-8 mb-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">
            Monitors Portal
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {assignedStudy
              ? "You’re assigned to the study below. Request the sections you need and the admin will approve."
              : "No study assignment yet. Please contact the admin to be assigned a study."}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold">
              {monitorName?.charAt(0) || "M"}
            </div>
            <div className="text-sm text-gray-800 font-medium">{monitorName}</div>
          </div>
          <button
            onClick={handleLogout}
            className="inline-flex items-center rounded-lg bg-gray-800 px-3 py-1.5 text-sm font-medium text-white shadow hover:bg-gray-900"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100"
      style={{
        backgroundImage: "url('/bg_ai.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundBlendMode: "soft-light",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mx-auto max-w-5xl px-4 py-8"
      >
        {/* Header */}
        {HeaderCard}

        {/* Assigned Study Card */}
        <div className="rounded-2xl bg-white/90 backdrop-blur shadow-lg ring-1 ring-black/5 p-6 md:p-8">
          {loadingAssign ? (
            <div className="text-gray-500">Loading assignment…</div>
          ) : !assignedStudy ? (
            <div className="rounded-xl bg-white/90 backdrop-blur p-8 shadow ring-1 ring-black/5">
              <p className="text-amber-700">
                You currently don’t have a study assignment. Once the admin assigns a study to your account, it will appear here.
              </p>
            </div>
          ) : (
            <>
              {/* Assigned Study Info */}
              <div className="mb-6 rounded-xl border border-gray-100 p-4 bg-white shadow-sm">
                <div className="text-sm text-gray-600">Assigned Study</div>
                <div className="text-lg font-semibold text-gray-900">
                  {assignedStudy.name || "Study"}{" "}
                  {assignedStudy.studyId ? (
                    <span className="text-gray-500">({assignedStudy.studyId})</span>
                  ) : null}
                </div>
              </div>

              {/* Checklist */}
              <div className="mb-4 text-sm text-gray-600">
                Check the sections you need access to:
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {SECTIONS.map((section) => {
                  const checked = checkedSections.includes(section);
                  return (
                    <label
                      key={section}
                      className={`flex items-center gap-3 rounded-lg border px-3 py-3 shadow-sm cursor-pointer transition
                        ${checked ? "border-indigo-300 bg-indigo-50/60" : "border-gray-200 bg-gray-50 hover:bg-gray-100"}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleCheckboxChange(section)}
                        className="h-5 w-5 text-indigo-600 accent-indigo-600"
                      />
                      <span className="text-gray-800 text-sm">{section}</span>
                    </label>
                  );
                })}
              </div>

              {/* Request Button */}
              <div className="mt-6">
                <button
                  onClick={handleRequest}
                  disabled={submitting}
                  className="inline-flex items-center rounded-lg bg-indigo-600 px-5 py-2.5 text-white font-medium shadow hover:bg-indigo-700 disabled:opacity-60"
                >
                  {submitting ? "Submitting…" : "Request Checked Documents"}
                </button>
              </div>
            </>
          )}
        </div>

        {/* My Requests (unchanged) */}
        <div className="mt-6 rounded-2xl bg-white/90 backdrop-blur shadow-lg ring-1 ring-black/5 p-6 md:p-8">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">My Requests</h2>
            <button
              onClick={loadMyRequests}
              className="text-sm text-indigo-700 hover:underline"
            >
              Refresh
            </button>
          </div>

          {loadingRequests ? (
            <div className="mt-4 text-gray-500">Loading…</div>
          ) : myRequests.length === 0 ? (
            <p className="mt-4 text-gray-600">No requests submitted yet.</p>
          ) : (
            <ul className="mt-4 divide-y divide-gray-100 rounded-lg border border-gray-100">
              {myRequests.map((r) => (
                <li
                  key={r.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium text-gray-900">
                        {r.studyName || "Study"}
                      </span>
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${statusChipClass(
                          r.status
                        )}`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            String(r.status || "").toLowerCase() === "approved"
                              ? "bg-green-500"
                              : String(r.status || "").toLowerCase() === "denied"
                              ? "bg-red-500"
                              : "bg-amber-500"
                          }`}
                        />
                        {r.status || "Pending"}
                      </span>
                    </div>
                    {Array.isArray(r.requestedSections) &&
                      r.requestedSections.length > 0 && (
                        <p className="mt-1 text-xs text-gray-500 truncate">
                          {r.requestedSections.join(" • ")}
                        </p>
                      )}
                  </div>

                  {String(r.status || "").toLowerCase() === "approved" ? (
                    <button
                      onClick={() => navigate(`/monitor-view/${r.id}`)}
                      className="shrink-0 inline-flex items-center rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      View Documents →
                    </button>
                  ) : (
                    <span className="shrink-0 text-sm text-gray-400">
                      Awaiting Admin Action
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </motion.div>
    </div>
  );
}
